import { CircleAlert, Search, UserPlus } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { DataTable } from "../components/DataTable";
import { Badge, Button, Card, Skeleton } from "../components/ui";
import { useAssetCustomers } from "../data/assets-platform-data";
import { type CustomerRow, useCustomers } from "../data/platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";

function tone(value: string): "success" | "warning" | "danger" | "neutral" {
  if (value === "ACTIVE" || value === "ATIVO") return "success";
  if (value === "WAITING_CARENCY" || value === "CARÊNCIA") return "warning";
  if (value === "SUSPENDED" || value === "SUSPENSO" || value === "OVERDUE") {
    return "danger";
  }
  return "neutral";
}

export function filterFamilyCustomers(
  rows: readonly CustomerRow[],
  search: string,
  seller: string,
  status: string,
) {
  const term = search.trim().toLocaleLowerCase("pt-BR");
  return rows.filter((customer) => {
    const normalizedStatus =
      customer.balance === "Vencido"
        ? "OVERDUE"
        : customer.funeral === "SUSPENSO"
          ? "SUSPENDED"
          : "ACTIVE";
    return (
      (!term ||
        [customer.name, customer.holder, customer.phone]
          .join(" ")
          .toLocaleLowerCase("pt-BR")
          .includes(term)) &&
      (seller === "TODOS" || customer.seller === seller) &&
      (status === "TODOS" || normalizedStatus === status)
    );
  });
}

export function CustomersPage() {
  const { hasModule } = useTenantRuntime();
  const assetVertical = hasModule("assets");
  const familyQuery = useCustomers();
  const assetQuery = useAssetCustomers();
  const query = assetVertical ? assetQuery : familyQuery;
  const [search, setSearch] = useState("");
  const [seller, setSeller] = useState("TODOS");
  const [status, setStatus] = useState("TODOS");
  const term = search.trim().toLocaleLowerCase("pt-BR");
  const familyRows = useMemo(
    () => filterFamilyCustomers(familyQuery.data ?? [], search, seller, status),
    [familyQuery.data, seller, status, search],
  );
  const assetRows = useMemo(
    () =>
      (assetQuery.data ?? []).filter((customer) => {
        return (
          (!term ||
            [customer.name, customer.document, customer.contact, customer.relationship]
              .join(" ")
              .toLocaleLowerCase("pt-BR")
              .includes(term)) &&
          (status === "TODOS" || customer.status === status)
        );
      }),
    [assetQuery.data, status, term],
  );
  const sellers = [
    ...new Set((familyQuery.data ?? []).map((customer) => customer.seller).filter(Boolean)),
  ];
  const visibleRows = assetVertical ? assetRows : familyRows;

  return (
    <Card
      title={assetVertical ? "Clientes e empresas" : "Base de famílias"}
      description={
        assetVertical
          ? "Compradores, locatários, motoristas e pessoas jurídicas em uma única base."
          : "Uma pessoa existe uma única vez e participa dos produtos por função."
      }
      action={
        <Link to={assetVertical ? "/operacoes/nova?tipo=counterparty" : "/clientes/novo"}>
          <Button>
            <UserPlus size={16} /> Novo cliente
          </Button>
        </Link>
      }
    >
      <div className="toolbar">
        <div className="search-box">
          <Search size={17} aria-hidden="true" />
          <input
            aria-label="Buscar clientes"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={
              assetVertical
                ? "Nome, CPF, CNPJ, contrato ou ativo"
                : "Nome, CPF, telefone ou contrato"
            }
          />
        </div>
        {!assetVertical && (
          <select
            aria-label="Filtrar responsável"
            value={seller}
            onChange={(event) => setSeller(event.target.value)}
          >
            <option value="TODOS">Todos os responsáveis</option>
            {sellers.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
        <select
          aria-label="Filtrar situação"
          value={status}
          onChange={(event) => setStatus(event.target.value)}
        >
          <option value="TODOS">Todos os status</option>
          <option value="ACTIVE">Ativo</option>
          <option value="OVERDUE">Vencido</option>
          <option value="SUSPENDED">Suspenso</option>
        </select>
        <select
          aria-label="Filtrar origem (indisponível na demonstração)"
          title="O filtro de origem será habilitado quando a origem estiver disponível na base."
          disabled
        >
          <option>Origem · em breve</option>
        </select>
      </div>

      {query.isLoading && (
        <div role="status" aria-label="Carregando clientes">
          <Skeleton className="table-skeleton" />
        </div>
      )}
      {query.error && (
        <div className="query-state query-error" role="alert">
          <CircleAlert size={20} aria-hidden="true" />
          <div>
            <strong>Não foi possível carregar os clientes.</strong>
            <span>{query.error.message}</span>
            <Button variant="secondary" onClick={() => void query.refetch()}>
              Tentar novamente
            </Button>
          </div>
        </div>
      )}

      {!query.isLoading && !query.error && visibleRows.length === 0 && (
        <div className="empty-state">
          <strong>
            {query.data?.length ? "Nenhum cliente encontrado" : "Nenhum cliente cadastrado"}
          </strong>
          <p>
            {query.data?.length
              ? "Ajuste a busca ou os filtros."
              : "Cadastre o primeiro cliente para começar a operação."}
          </p>
          {query.data?.length ? (
            <Button
              variant="secondary"
              onClick={() => {
                setSearch("");
                setSeller("TODOS");
                setStatus("TODOS");
              }}
            >
              Limpar filtros
            </Button>
          ) : null}
        </div>
      )}

      {!query.isLoading && !query.error && assetVertical && assetRows.length > 0 && (
        <DataTable
          rows={assetRows}
          getKey={(customer) => customer.id}
          columns={[
            {
              key: "name",
              header: "Cliente",
              render: (customer) => (
                <Link className="strong-link" to={`/clientes/${customer.id}`}>
                  {customer.name}
                  <small>
                    {customer.documentType} · {customer.document}
                  </small>
                </Link>
              ),
            },
            {
              key: "contact",
              header: "Contato",
              render: (customer) => customer.contact,
            },
            {
              key: "relationship",
              header: "Relação",
              render: (customer) => customer.relationship,
            },
            {
              key: "origin",
              header: "Origem",
              render: () => <span className="muted">Origem não informada</span>,
            },
            {
              key: "agreements",
              header: "Contratos ativos",
              render: (customer) => customer.activeAgreements,
            },
            {
              key: "balance",
              header: "Saldo aberto",
              render: (customer) => customer.openBalance,
            },
            {
              key: "status",
              header: "Situação",
              render: (customer) => <Badge tone={tone(customer.status)}>{customer.status}</Badge>,
            },
          ]}
        />
      )}

      {!query.isLoading && !query.error && !assetVertical && familyRows.length > 0 && (
        <DataTable
          rows={familyRows}
          getKey={(customer) => customer.id}
          columns={[
            {
              key: "name",
              header: "Família",
              render: (customer) => (
                <Link className="strong-link" to={`/clientes/${customer.id}`}>
                  {customer.name}
                  <small>{customer.holder}</small>
                </Link>
              ),
            },
            {
              key: "phone",
              header: "Contato",
              render: (customer) => customer.phone,
            },
            {
              key: "funeral",
              header: "Funeral",
              render: (customer) => <Badge tone={tone(customer.funeral)}>{customer.funeral}</Badge>,
            },
            {
              key: "telemedicine",
              header: "Telemedicina",
              render: (customer) => (
                <Badge tone={tone(customer.telemedicine)}>{customer.telemedicine}</Badge>
              ),
            },
            {
              key: "seller",
              header: "Vendedor",
              render: (customer) => customer.seller,
            },
            {
              key: "origin",
              header: "Origem",
              render: () => <span className="muted">Origem não informada</span>,
            },
            {
              key: "balance",
              header: "Financeiro",
              render: (customer) => (
                <Badge tone={customer.balance === "Em dia" ? "success" : "danger"}>
                  {customer.balance}
                </Badge>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}
